/**
 * Les feuilles d'entraînement d'un enfant : celle en cours, puis les closes.
 *
 * Hedi, le 25 septembre 2026 : *« Un entrainement peut être crée, il est donc
 * éditable : on peut lui rajouter des exercices. Une fois l'entrainement clos,
 * on ne peut plus lui ajouter des exercices et ne peux plus être réouvert. »*
 *
 * **Clore est le geste qui envoie.** Tant que la feuille est ouverte, l'enfant
 * ne la voit pas — arbitrage du même jour : la voir s'allonger d'un chapitre à
 * l'autre, c'est commencer un travail dont on ne connaît pas la fin. L'écran le
 * dit en toutes lettres plutôt que de le laisser deviner, parce que c'est la
 * seule chose ici qu'un parent ne peut pas voir à l'usage : une feuille oubliée
 * ouverte n'a pas l'air différente d'une feuille envoyée.
 *
 * **La confirmation est rendue dans la page, jamais par `window.confirm`.** Le
 * `WebChromeClient` de la coquille Android ne surcharge que `onShowFileChooser`,
 * si bien qu'une boîte du navigateur y est un refus silencieux — un bouton qui
 * ne fait rien sur le seul appareil où le système tourne. Elle est ici doublement
 * due : clore ne se défait pas.
 *
 * **Le corrigé de l'éditeur est servi ici** et seulement ici, par un chemin de
 * signature distinct de celui de l'enfant (`web-pwa` §17.4) : c'est ce que le
 * parent vient faire — *« valider que le travail est correct en regardant la
 * correction »* (Hedi, 24 septembre 2026). Côté enfant, la clé ne traverse pas,
 * et un test l'affirme sur le texte HTTP brut des deux côtés.
 *
 * Les deux PDF sont de simples liens : l'authentification est un cookie, donc
 * le navigateur les ouvre lui-même et c'est son lecteur qui imprime.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { buildUrl, call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { EntrainementOut, LigneDEntrainementOut } from "@/api/schema";
import { enClair } from "@/ui/dates";
import { AsseoirACote, peutSAsseoir } from "@/ui/asseoir";
import { Decoupe, pagesDe } from "@/ui/Decoupe";
import { useEnfants } from "@/ui/enfants";
import { etiquette } from "@/ui/matieres";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import {
  composeeParLEleve,
  etatDeLaFeuille,
  leParentPeutAgir,
  mentionDOrigine,
  parChapitre,
} from "@/ui/feuille";
import { entrainementsDe } from "@/v2/chemins";
import { Depliable } from "@/v2/Depliable";
import { DicteeDeLaSemaine, TexteDuParent } from "@/v2/FormePapier";

/** Le verdict d'un parent, dans ses mots et non dans ceux de la base. */
const VERDICTS: Record<string, string> = {
  juste: "juste",
  moitie: "à moitié",
  faux: "faux",
};

/**
 * Où en est une feuille, dans la ligne de résumé.
 *
 * « Corrigée » veut dire **chaque exercice a reçu un verdict du parent** — c'est
 * `etatDeLaFeuille`, déjà lu par l'écran de validation, et rien d'autre : les
 * lignes renvoyées par le serveur portent `verdict`, et une feuille dont une
 * seule ligne n'en a pas attend encore une relecture. Une feuille ouverte n'est
 * ni l'un ni l'autre : l'enfant ne l'a pas reçue.
 */
export const ETAT_EN_MOTS: Record<ReturnType<typeof etatDeLaFeuille>, string> = {
  ouverte: "pas encore envoyée à l'enfant",
  attend: "pas encore corrigée",
  relue: "corrigée",
};

/**
 * L'état d'une feuille en mots, selon qui l'a composée.
 *
 * Une feuille ouverte du parent n'est « pas encore envoyée » ; une feuille
 * ouverte de l'élève est **chez lui** — il la voit, la remplit, et la clôt
 * quand il veut. Dire « pas encore envoyée à l'enfant » d'une feuille que
 * l'enfant a lui-même composée serait faux, et ferait chercher un bouton
 * « Clore » que l'écran ne propose justement pas.
 */
export function etatEnMots(
  feuille: Pick<EntrainementOut, "origine" | "closed_at" | "lignes">,
  prenom: string | undefined,
): string {
  const etat = etatDeLaFeuille(feuille);
  if (etat === "ouverte" && composeeParLEleve(feuille)) {
    return `en cours chez ${prenom ?? "l'enfant"}`;
  }
  return ETAT_EN_MOTS[etat];
}

/** Sous le titre d'une feuille de l'élève : ce que le parent peut et ne peut
 *  pas y faire, et pourquoi. */
export function phraseDeLaFeuilleDeLEleve(
  feuille: Pick<EntrainementOut, "closed_at">,
  prenom: string | undefined,
): string {
  const qui = prenom ?? "L'enfant";
  return feuille.closed_at
    ? `${qui} l'a composée puis close, et a vu tous les corrigés en la clôturant.`
    : `${qui} la compose et la travaille en ce moment, et la clôt quand bon lui semble pour lire la correction. Vous la lisez, sans y ajouter ni en retirer d'exercice.`;
}

function intituleDe(ligne: LigneDEntrainementOut): string {
  return `${ligne.numero ? `Exercice ${ligne.numero}` : `Exercice ${ligne.rang}`}${
    ligne.page ? ` — page ${ligne.page}` : ""
  }`;
}

function Ligne({
  ligne,
  retirer,
  asseoir,
}: {
  ligne: LigneDEntrainementOut;
  retirer?: () => void | Promise<void>;
  /** L'enfant de la séance à deux, quand elle a un sens sur cette ligne (lot P4). */
  asseoir?: string;
}) {
  const etat = ligne.verdict
    ? (VERDICTS[ligne.verdict] ?? ligne.verdict)
    : ligne.photo_at
      ? `rendu le ${enClair(ligne.photo_at)}`
      : "en attente";
  const intitule = intituleDe(ligne);

  return (
    <li className="list__item list__item--pile">
      <div>
        <strong>{intitule}</strong>
        <p className="muted small">{ligne.competence}</p>
        <p className="muted small">{etat}</p>
      </div>
      {/* Le découpé, comme sur le sommaire (Hedi, 25 septembre 2026) : un
          exercice d'ouvrage n'a pas de texte de notre côté, donc sans l'image
          le parent relit une liste de numéros. Le corrigé reste un lien — il
          se regarde en vis-à-vis de la photo, sur l'écran de validation. */}
      <Decoupe urls={pagesDe(ligne.enonce_urls, ligne.enonce_url)} intitule={intitule} />
      {/* Une forme sur cahier : consigne, durée, grille, et pour une dictée le
          texte en groupes de souffle (lot P3). */}
      <TexteDuParent ligne={ligne} />
      <div className="list__actions">
        {ligne.photo_url ? (
          <a href={ligne.photo_url} target="_blank" rel="noreferrer">
            La photo
          </a>
        ) : null}
        {pagesDe(ligne.corrige_urls, ligne.corrige_url).map((url, rang, pages) => (
          <a key={`${rang}-${url}`} href={url} target="_blank" rel="noreferrer">
            {pages.length > 1 ? `Le corrigé (${rang + 1}/${pages.length})` : "Le corrigé"}
          </a>
        ))}
        {asseoir ? <AsseoirACote childId={asseoir} cible={{ ligneId: ligne.ligne_id }} /> : null}
        {/* Retirer n'existe que tant que la feuille est ouverte : après, la
            ligne est sur une feuille imprimée, et peut-être déjà faite. */}
        {retirer ? (
          <button type="button" className="btn btn--quiet" onClick={() => void retirer()}>
            Retirer
          </button>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Clore, en deux temps.
 *
 * La phrase dit **ce que ça emporte** et non « vous confirmez ? » : l'enfant la
 * reçoit, plus rien ne s'y ajoute, et il n'y a pas de retour. Un parent qui a
 * cliqué par erreur ne peut que composer une seconde feuille.
 */
function Clore({ combien, clore }: { combien: number; clore: () => Promise<void> }) {
  const [etat, setEtat] = useState<"ferme" | "confirme" | "envoi">("ferme");

  if (etat === "ferme") {
    return (
      <Button type="button" onClick={() => setEtat("confirme")}>
        Clore et envoyer à l'enfant
      </Button>
    );
  }

  return (
    <div className="notice">
      <p>
        Clore envoie ces {combien} exercice{combien > 1 ? "s" : ""} à l'enfant. Ensuite, plus rien
        ne s'y ajoute et la feuille ne se rouvre pas.
      </p>
      <div className="screen__actions">
        <Button
          type="button"
          disabled={etat === "envoi"}
          onClick={() => {
            setEtat("envoi");
            void clore().catch(() => setEtat("confirme"));
          }}
        >
          {etat === "envoi" ? "Envoi…" : "Oui, clore"}
        </Button>
        <Button type="button" variant="quiet" onClick={() => setEtat("ferme")}>
          Annuler
        </Button>
      </div>
    </div>
  );
}

function Feuille({
  feuille,
  childId,
  prenom,
  recharger,
}: {
  feuille: EntrainementOut;
  childId: string;
  prenom: string | undefined;
  recharger: () => void;
}) {
  const lignes = feuille.lignes ?? [];
  const photographiees = feuille.photographiees ?? 0;
  const etat = etatDeLaFeuille(feuille);
  const ouverte = etat === "ouverte";
  // Une feuille composée par l'élève (5 octobre 2026) se lit ici et ne se
  // touche pas : ouverte, elle est en cours chez lui, et c'est lui qui la clôt
  // quand il veut lire la correction (`leParentPeutAgir`).
  const deLEleve = composeeParLEleve(feuille);
  const agissable = leParentPeutAgir(feuille);
  const [erreur, setErreur] = useState<string | null>(null);

  const agir = async (action: () => Promise<unknown>) => {
    setErreur(null);
    try {
      await action();
      recharger();
    } catch (echec) {
      setErreur(echec instanceof ApiError ? echec.message : "L'action a échoué.");
      throw echec;
    }
  };

  const pdf = (quoi: "imprimerLaFeuille" | "imprimerLeCorrige") =>
    buildUrl(quoi, { params: { child_id: childId, entrainement_id: feuille.id } });

  // Les deux PDF sont sur la ligne de résumé et non dans le détail : c'est ce
  // qu'un parent vient chercher le plus souvent, pour imprimer.
  const liens = (
    <>
      <a
        className="btn btn--quiet"
        href={pdf("imprimerLaFeuille")}
        target="_blank"
        rel="noreferrer"
      >
        La feuille en PDF
      </a>
      <a
        className="btn btn--quiet"
        href={pdf("imprimerLeCorrige")}
        target="_blank"
        rel="noreferrer"
      >
        Le corrigé en PDF
      </a>
    </>
  );

  return (
    <Depliable
      titre={
        (deLEleve ? `${mentionDOrigine(feuille, prenom)} — ` : "") +
        (ouverte
          ? `${deLEleve ? "en cours" : "Feuille en cours"}, commencée le ${enClair(feuille.composed_at)}`
          : `${deLEleve ? "close" : "Feuille close"} le ${enClair(feuille.closed_at as string)}`)
      }
      etat={etatEnMots(feuille, prenom)}
      liens={liens}
    >
      <p className="muted small">
        Commencée le {enClair(feuille.composed_at)} — {lignes.length} exercice
        {lignes.length > 1 ? "s" : ""}
        {ouverte ? null : ` — ${photographiees} rendu${photographiees > 1 ? "s" : ""}`}
      </p>
      {ouverte && !deLEleve ? (
        <p className="muted small">
          L'enfant ne la voit pas encore. Ajoutez-y les chapitres que vous voulez, puis clôturez-la.
        </p>
      ) : null}
      {deLEleve ? (
        <p className="muted small">{phraseDeLaFeuilleDeLEleve(feuille, prenom)}</p>
      ) : null}

      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}

      {parChapitre(lignes).map((bloc) => (
        <section key={`${bloc.ouvrageTitre}/${bloc.chapitre}`}>
          <p className="card__subtitle">{bloc.titre}</p>
          <p className="muted small">
            {bloc.ouvrageTitre} — {etiquette(bloc.matiere, bloc.matiereLibelle)}
          </p>
          <ul className="list">
            {bloc.lignes.map((ligne) => (
              <Ligne
                key={ligne.ligne_id}
                ligne={ligne}
                asseoir={peutSAsseoir(ligne, !ouverte) ? childId : undefined}
                retirer={
                  agissable
                    ? () =>
                        agir(() =>
                          call("retirerUneLigneDEntrainement", {
                            params: { child_id: childId, ligne_id: ligne.ligne_id },
                          }),
                        )
                    : undefined
                }
              />
            ))}
          </ul>
        </section>
      ))}

      {agissable && lignes.length > 0 ? (
        <Clore
          combien={lignes.length}
          clore={() =>
            agir(() => call("cloreLEntrainement", { params: { child_id: childId } })).then(
              () => undefined,
            )
          }
        />
      ) : null}
      {etat === "attend" ? (
        <p className="muted small">
          <Link to={`${entrainementsDe(childId)}/a-valider`}>Relire ce qui a été rendu</Link>
        </p>
      ) : null}
    </Depliable>
  );
}

export function EntrainementsScreen() {
  const { childId = "" } = useParams();
  const enfants = useEnfants();
  const prenom = enfants.find((enfant) => enfant.child_id === childId)?.display_name;
  const [entrainements, setEntrainements] = useState<EntrainementOut[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [tour, setTour] = useState(0);

  const charger = useCallback(async () => {
    const charge = await call("entrainementsDeLEnfant", { params: { child_id: childId } });
    return charge.entrainements ?? [];
  }, [childId]);

  useEffect(() => {
    let annule = false;
    setErreur(null);
    void (async () => {
      try {
        const lus = await charger();
        if (!annule) setEntrainements(lus);
      } catch (echec) {
        if (!annule) {
          setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible.");
        }
      }
    })();
    return () => {
      annule = true;
    };
  }, [charger, tour]);

  return (
    <Screen
      title="Les entraînements composés"
      lead={
        prenom ? `Ce qui a été préparé pour ${prenom} : la feuille en cours d'abord` : undefined
      }
    >
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!entrainements && !erreur ? <Loading /> : null}

      <DicteeDeLaSemaine
        childId={childId}
        prenom={prenom}
        apresDon={() => setTour((avant) => avant + 1)}
      />

      {entrainements && entrainements.length === 0 ? (
        <Card>
          <Empty>
            Rien n'a encore été préparé. Ouvrez un cahier, choisissez un chapitre, et cochez les
            exercices.
          </Empty>
        </Card>
      ) : null}

      {(entrainements ?? []).map((feuille) => (
        <Feuille
          key={feuille.id}
          feuille={feuille}
          childId={childId}
          prenom={prenom}
          recharger={() => setTour((avant) => avant + 1)}
        />
      ))}
    </Screen>
  );
}
